import { Box } from '@mui/material';
import {
    EmailSubsectionChip,
    IconToleranceTitleClock,
    PhoneLight,
    SecurityIcon
} from '../../../../components/buttons/IconButtons';
import { CustomStyleIconCircleContainer } from '../../../../components/Styles';
import ExpandedRowItem from '../Subcomponents/ExpandedRowItem';
import {CustomTextField} from "../../../../components/CustomTextField";
import {CustomSwitch} from "../../../../components/CustomSwitch";
import React from "react";
import {MIN_MAX} from "../../../../enums/MinMaxEnum";


export const SETTING_CONFIG_MODEL = {
    enableOtp: false,
    hasApplication: false,
    lastModifiedByFullName: '',
    lastModifiedAt: null,
    resendInvitationHour: 1,
    accessTolerancePeriodicMin: 0,
    accessToleranceJustOnceMin: 0,
    set: function (data) {
        return {
            ...this,
            enableOtp: data?.enableOtp,
            hasApplication: data?.hasApplication,
            lastModifiedByFullName: data?.lastModifiedByFullName,
            lastModifiedAt: data?.lastModifiedAt,
            resendInvitationHour: data?.resendInvitationHour,
            accessTolerancePeriodicMin: data?.accessTolerancePeriodicMin,
            accessToleranceJustOnceMin: data?.accessToleranceJustOnceMin,
        };
    },
    toRegister: function () {
        return {
            enableOtp: this.enableOtp,
            hasApplication: this.hasApplication,
            resendInvitationHour: parseInt(this.resendInvitationHour, 10),
            accessTolerancePeriodicMin: parseInt(this.accessTolerancePeriodicMin, 10),
            accessToleranceJustOnceMin: parseInt(this.accessToleranceJustOnceMin, 10)
        };
    },
    isValid: function () {
        const atLeast = (value, min) =>
            value !== '' && value != null && !isNaN(Number(value)) && Number(value) >= min;

        return atLeast(this.accessTolerancePeriodicMin, MIN_MAX.ACCESS_TOLERANCE.start)
            && atLeast(this.accessToleranceJustOnceMin, MIN_MAX.ACCESS_TOLERANCE.start)
            && atLeast(this.resendInvitationHour, MIN_MAX.RESEND_INVITATION_HOUR.start);
    },
};

export const SYSTEM_CONFIG_LIST = {
    environmentUrl: 'environmentUrl',
    enableOtp: 'enableOtp',
    hasApplication: 'hasApplication',
    resendInvitationHour: 'resendInvitationHour',
    accessTolerancePeriodicMin: 'accessTolerancePeriodicMin',
    accessToleranceJustOnceMin: 'accessToleranceJustOnceMin',
};

export const getSettingConfigColumns = ({ t }) => [
    {
        field: 'label',
        label: 'item',
        classTableCell: 'w-100',
        render: (val, row) => (
            <div className='d-flex align-items-center '>
                <Box sx={CustomStyleIconCircleContainer} className={"background-general-00"}>{row.icon}</Box>

                <p className='fs-14px font-color-text-950 ibl-font-w-600 m-0 ml-3'>{t(val)}</p>
            </div>
        ),
    },
];

export const getSettingConfigRows = ({
    t,
    settingConfig,
    handleChange,
    disabled,
    emptyFields = false,
    isValidPeriodic = true,
    isValidJustOnce = true,
    isValidResend = true,
}) => [
    {
        id: 1,
        key: 'otpVerification',
        label: 'otp_verification',
        icon: <PhoneLight className='w-16px h-16px' />,
        status: true,
        content: () => (
            <ExpandedRowItem
                title={t('enable_otp_verification')}
                description={t('require_one_time_password_for_user_authentication')}
            >
                <CustomSwitch
                    id='cb_scm_enable_otp'
                    name='enableOtp'
                    checked={settingConfig?.enableOtp}
                    disabled={disabled}
                    showBorder={false}
                    onChange={(e) => handleChange({ target: { name: e.target.name, value: !settingConfig?.enableOtp } })}
                />
            </ExpandedRowItem>
        ),
    },
    {
        id: 2,
        key: 'mobileApp',
        label: 'mobile_app',
        icon: <SecurityIcon className='w-16px h-16px' />,
        status: false,
        content: () => (
            <ExpandedRowItem
                title={t('application')}
                description={t('enable_access_via_mobile_application')}
            >
                <CustomSwitch
                    id='cb_scm_has_application'
                    name='hasApplication'
                    checked={settingConfig?.hasApplication}
                    disabled={disabled}
                    showBorder={false}
                    onChange={(e) => handleChange({ target: { name: e.target.name, value: !settingConfig?.hasApplication } })}
                />
            </ExpandedRowItem>
        ),
    },
    {
        id: 3,
        key: 'accessTolerance',
        label: 'access_tolerance',
        icon: <IconToleranceTitleClock className='w-16px h-16px' />,
        status: true,
        content: () => (
          <>
              <ExpandedRowItem
                  title={t('periodic_access')}
                  description={t('periodic_access_tolerance_description')}
              >
                  <CustomTextField id={"txt_scm_access_tolerance_periodic_min"}
                                   length={2}
                                   name={"accessTolerancePeriodicMin"}
                                   type={'number'}
                                   required
                                   emptyFields={emptyFields}
                                   disabled={disabled}
                                   isValid={isValidPeriodic}
                                   isValidText={t('it_must_be_greater_than_or_equal_to', { min: MIN_MAX.ACCESS_TOLERANCE.start })}
                                   helperText={t('it_must_be_greater_than_or_equal_to', { min: MIN_MAX.ACCESS_TOLERANCE.start })}
                                   inputProps={{
                                       min: MIN_MAX.ACCESS_TOLERANCE.start,
                                       max: MIN_MAX.ACCESS_TOLERANCE.end
                                   }}
                                   value={settingConfig.accessTolerancePeriodicMin}
                                   onChange={(evt) => {
                                       let value = evt.target.value;
                                       if (value !== '') {
                                           value = String(Math.min(MIN_MAX.ACCESS_TOLERANCE.end, parseInt(value, 10)));
                                       }
                                       handleChange({ target: { value, name: "accessTolerancePeriodicMin" } }, "accessTolerancePeriodicMin");
                                   }}
                                   suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('min')}</p>}
                  />
              </ExpandedRowItem>
              <div className="border-bottom-shades-300 my-2" />
              <ExpandedRowItem
                  title={t('one_time_access')}
                  description={t('one_time_access_tolerance_description')}
              >
                  <CustomTextField id={"txt_scm_access_tolerance_just_once_min"}
                                   length={2}
                                   name={"accessToleranceJustOnceMin"}
                                   type={'number'}
                                   required
                                   emptyFields={emptyFields}
                                   disabled={disabled}
                                   isValid={isValidJustOnce}
                                   isValidText={t('it_must_be_greater_than_or_equal_to', { min: MIN_MAX.ACCESS_TOLERANCE.start })}
                                   helperText={t('it_must_be_greater_than_or_equal_to', { min: MIN_MAX.ACCESS_TOLERANCE.start })}
                                   inputProps={{
                                       min: MIN_MAX.ACCESS_TOLERANCE.start,
                                       max: MIN_MAX.ACCESS_TOLERANCE.end
                                   }}
                                   value={settingConfig.accessToleranceJustOnceMin}
                                   onChange={(evt) => {
                                       let value = evt.target.value;
                                       if (value !== '') {
                                           value = String(Math.min(MIN_MAX.ACCESS_TOLERANCE.end, parseInt(value, 10)));
                                       }
                                       handleChange({ target: { value, name: "accessToleranceJustOnceMin" } }, "accessToleranceJustOnceMin");
                                   }}
                                   suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('min')}</p>}
                  />
              </ExpandedRowItem>
          </>
        ),
    },
    {
        id: 4,
        key: 'emailValidationReminder',
        label: 'email_validation_reminder',
        icon: <EmailSubsectionChip className='w-16px h-16px' />,
        status: true,
        content: () => (
            <>
                <ExpandedRowItem
                    title={t('send_time')}
                    description={t('email_validation_reminder_description')}
                >
                    <CustomTextField id={"txt_scm_email_validation_reminder_description"}
                                     length={2}
                                     name={"resendInvitationHour"}
                                     type={'number'}
                                     required
                                     emptyFields={emptyFields}
                                     isValid={isValidResend}
                                     disabled={disabled}
                                     isValidText={t('it_must_be_greater_than_or_equal_to', { min: MIN_MAX.RESEND_INVITATION_HOUR.start })}
                                     helperText={t('it_must_be_greater_than_or_equal_to', { min: MIN_MAX.RESEND_INVITATION_HOUR.start })}
                                     inputProps={{
                                         min: MIN_MAX.RESEND_INVITATION_HOUR.start,
                                         max: MIN_MAX.RESEND_INVITATION_HOUR.end
                                     }}
                                     value={settingConfig.resendInvitationHour}
                                     onChange={(evt) => {
                                         let value = evt.target.value;
                                         if (value !== '') {
                                             value = String(Math.min(MIN_MAX.RESEND_INVITATION_HOUR.end, parseInt(value, 10)));
                                         }
                                         handleChange({ target: { value, name: "resendInvitationHour" } }, "resendInvitationHour");
                                     }}
                                     suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('hrs')}</p>}
                    />
                </ExpandedRowItem>
            </>
        ),
    },
];
